import { URLKeys, noddiAsync } from 'noddi-async';
import { UserData, UserDataProps } from 'noddi-types';
import { commonTexts, errorCodesEnglish, trimAllWhiteCharacters } from 'noddi-util';
import { LanguageCodeType, useAuthContext } from 'noddi-web';
import { useState } from 'react';

import { cn } from 'noddi-ui-common';

import { ErrorCodes } from '../../Elements';
import { PhoneNumberForm } from './PhoneNumberForm';
import { VerificationCodeForm } from './VerificationCodeForm';

const userAlreadyExists = (data: UserDataProps) => {
  const token = data.token;
  return token && token.length > 0;
};

type Props = {
  userRegistrationForm: (phoneNumber: string, onLoginSuccess?: (userData: UserDataProps) => void) => React.ReactElement;
  className?: string;
  hidePhoneNumberText?: boolean;
  trackUserIdentity?: (userData: UserData) => void;
  language?: LanguageCodeType;
  onLoginSuccess?: (userData: UserDataProps) => void;
  translations?: {
    enterCode: string;
    confirm: string;
    enterNumberToReceiveVerificationCode: string;
    resend: string;
    sendCode: string;
    didntReceiveSms: string;
  };
  errorCodes?: ErrorCodes;
};

// Since the worker app and the customer app are using the same codebase, we need to
// allow for different user registration forms. This is why provide a callback function
const SMSLogin = ({
  userRegistrationForm,
  className,
  hidePhoneNumberText,
  trackUserIdentity,
  onLoginSuccess,
  translations,
  language,
  errorCodes
}: Props) => {
  const { loginUser } = useAuthContext();
  const [mobileNumber, setMobileNumber] = useState('');
  const [codeSentSuccessfully, setCodeSentSuccessfully] = useState(false);

  const texts = translations ?? commonTexts.smsLoginTranslations;
  const errorCodeTexts = errorCodes ?? errorCodesEnglish;
  const {
    mutateAsync,
    isPending,
    isSuccess: verificationSuccess,
    data,
    error
  } = noddiAsync.usePost({
    type: URLKeys.postVerifyPhoneNumber,
    queryConfig: {
      onSuccess(data) {
        if (userAlreadyExists(data.data)) {
          trackUserIdentity?.(data.data.user);
          loginUser(data.data);
          onLoginSuccess?.(data.data);
        }
      }
    }
  });

  const phoneNumberDto = ({ urlEncode = false }: { urlEncode?: boolean }) => {
    const serializedMobileNumber = trimAllWhiteCharacters(mobileNumber);

    return urlEncode ? encodeURIComponent(serializedMobileNumber) : serializedMobileNumber;
  };

  const showSignUpForm = verificationSuccess && !userAlreadyExists(data.data);

  return (
    <>
      {showSignUpForm ? (
        userRegistrationForm(phoneNumberDto({}), onLoginSuccess)
      ) : (
        <div className={cn('flex flex-col gap-10', className)}>
          <PhoneNumberForm
            setCodeSentSuccessfully={setCodeSentSuccessfully}
            mobileNumber={mobileNumber}
            setMobileNumber={setMobileNumber}
            hideText={hidePhoneNumberText}
            translations={texts}
            language={language}
            errorCodes={errorCodeTexts}
          />
          {codeSentSuccessfully && (
            <VerificationCodeForm
              translations={texts}
              errorCodes={errorCodeTexts}
              error={error}
              isLoading={isPending}
              phoneNumber={phoneNumberDto({})}
              verificationSuccess={verificationSuccess}
              onClick={({ code, phoneNumber }: { code: string; phoneNumber: string }) => {
                mutateAsync({ code, phoneNumber });
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

export { SMSLogin };
